import { useCallback, useContext, useEffect, useState } from "react";
import { Button, Checkbox, Loading, Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ContextEDH } from "@context/ContextEDH";
import useContextUrl from "@common/hooks/useContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { usePackageWithLeaguesCreationService } from "@services/domains/packages";
import type { DataReducerType } from "../../../context/types";
import PackageDetailsFields, { type SelectedPopulationOption } from "../package-details-fields";
import LeagueConfigurationStep from "../league-configuration-step";
import InitialGroupsStep from "../initial-groups-step";
import {
  selectCanUseJointCreation,
  selectShouldVisitInitialGroups,
} from "../../store/packageWithLeaguesStore";
import usePackageWithLeaguesStore from "../../hooks/use-package-with-leagues-store";
import usePackageWithLeaguesStoreApi from "../../hooks/use-package-with-leagues-store-api";
import usePackageWithLeaguesCreation from "../../hooks/use-package-with-leagues-creation";
import css from "./packageCreationWizard.module.scss";
import { buildPackageOnlyCreationData } from "../../utils/package-creation-data";
import { buildPackageViewSearch } from "@dimensions/package/utils";

interface PackageCreationWizardProps {
  isPackageOnlyLoading: boolean;
  onCancel: () => void;
  onPackageOnlySubmit: (data: DataReducerType) => Promise<void>;
}

const PackageCreationWizard = ({
  isPackageOnlyLoading,
  onCancel,
  onPackageOnlySubmit,
}: PackageCreationWizardProps) => {
  const { t } = useTranslation();
  const { searchPopulations } = usePackageWithLeaguesCreationService();
  const history = useHistory();
  const { scenario, origin } = useContextUrl();
  const { editorUrl } = useNavigationUrl();
  const { state } = useContext(ContextEDH);
  const store = usePackageWithLeaguesStoreApi();
  const currentStep = usePackageWithLeaguesStore(value => value.currentStep);
  const packageDetails = usePackageWithLeaguesStore(value => value.packageDetails);
  const isJointCreationSelected = usePackageWithLeaguesStore(
    value => value.isJointCreationSelected,
  );
  const canUseJointCreation = usePackageWithLeaguesStore(selectCanUseJointCreation);
  const shouldVisitInitialGroups = usePackageWithLeaguesStore(selectShouldVisitInitialGroups);
  const [selectedPopulation, setSelectedPopulation] = useState<SelectedPopulationOption | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const user = state?.base?.base?.user;
    const abilities = user?.abilities;
    store.getState().configureFlow(
      {
        canCreate: Boolean(user?.permissions?.create),
        canEditPackages: Boolean(abilities?.can_edit_packages),
        canEditLinksAndSections: Boolean(abilities?.can_edit_links_and_sections),
        canCreateGroupsInBundleView: Boolean(abilities?.can_create_groups_in_bundle_view),
      },
      {
        groupCreationMode: state?.base?.createGroupWithinLinkCreation ?? "DISABLED",
        canCreateLinkWithoutNewSections: Boolean(state?.base?.canCreateLinkWithoutNewSections),
      },
    );
  }, [state?.base, store]);

  const populationSelectKey = [
    packageDetails.termId,
    packageDetails.campusId,
    packageDetails.modalityId,
    packageDetails.programId,
  ].join("|");

  useEffect(() => {
    setSelectedPopulation(null);
    setSearchTerm("");
    setPage(0);
    store.getState().setPackageDetails({ population: null });
  }, [populationSelectKey, store]);

  const loadPopulationOptions = useCallback(
    async (newSearchTerm: string) => {
      const nextPage = searchTerm === newSearchTerm ? page + 1 : 1;
      setSearchTerm(newSearchTerm);
      setPage(nextPage);

      const result = await searchPopulations({
        termId: packageDetails.termId,
        scenarioId: scenario,
        originId: origin,
        campusId: packageDetails.campusId,
        modalityId: packageDetails.modalityId,
        programId: packageDetails.programId,
        page: nextPage,
        searchTerm: newSearchTerm,
      });
      const options: SelectedPopulationOption[] = result.items.map(population => ({
        label: population.code,
        value: population.id,
        index: population.packageIndex + 1,
        self: population,
      }));

      return {
        options,
        hasMore: result.pageInfo?.hasNextPage,
        additional: { page: nextPage },
      };
    },
    [
      origin,
      packageDetails.campusId,
      packageDetails.modalityId,
      packageDetails.programId,
      packageDetails.termId,
      page,
      scenario,
      searchPopulations,
      searchTerm,
    ],
  );

  const handlePopulationChange = (option: SelectedPopulationOption | null) => {
    setSelectedPopulation(option);
    store.getState().setPackageDetails({
      population: option
        ? {
            id: option.self.id,
            code: option.self.code,
            index: option.index,
            programId: option.self.program?.id,
            period: option.self.term?.code ?? "",
            campus: option.self.campus?.code ?? "",
            modality: option.self.modality?.name ?? "",
            level: String(option.self.level ?? ""),
            program: option.self.program?.name ?? "",
            curriculum: option.self.curriculum ?? "",
          }
        : null,
    });
  };

  const isPackageDetailsValid = Boolean(
    packageDetails.termId && packageDetails.population && packageDetails.packageName.trim(),
  );
  const canSelectPopulation = Boolean(
    packageDetails.termId &&
    (packageDetails.campusId || packageDetails.modalityId || packageDetails.programId),
  );

  const { submit, isLoading: isJointLoading } = usePackageWithLeaguesCreation({
    scenarioId: scenario,
    originId: origin,
    store,
    onSuccess: outcome => {
      if (!outcome.packageId) return;
      const packageId = outcome.packageId;
      store.getState().reset();
      history.push(`${editorUrl("package", packageId)}${buildPackageViewSearch("", "leagues")}`);
    },
  });

  const handleStepOneContinue = async () => {
    if (!isPackageDetailsValid || !selectedPopulation) return;
    if (isJointCreationSelected) {
      store
        .getState()
        .prepareLeagueConfiguration([packageDetails.termId, selectedPopulation.self.id].join("|"));
      store.getState().goForward();
      return;
    }
    await onPackageOnlySubmit(
      buildPackageOnlyCreationData(
        selectedPopulation.self,
        packageDetails.termId,
        packageDetails.packageName,
      ),
    );
  };

  const isLoading = isPackageOnlyLoading || isJointLoading;

  return (
    <section className={css.packageCreationWizard} data-step={currentStep}>
      <div hidden={currentStep !== "package-details"}>
        {/* Keep this step mounted so going back preserves the async-select state. */}
        <PackageDetailsFields
          selectedCampus={packageDetails.campusId}
          selectedModality={packageDetails.modalityId}
          selectedPopulation={selectedPopulation}
          packageName={packageDetails.packageName}
          populationSelectKey={populationSelectKey}
          canSelectPopulation={canSelectPopulation}
          loadPopulationOptions={loadPopulationOptions}
          onTermChange={termId => store.getState().setPackageDetails({ termId })}
          onCampusChange={campusId => store.getState().setPackageDetails({ campusId })}
          onModalityChange={modalityId => store.getState().setPackageDetails({ modalityId })}
          onProgramChange={programId => store.getState().setPackageDetails({ programId })}
          onPopulationChange={handlePopulationChange}
          onPopulationMenuClose={() => {
            setSearchTerm("");
            setPage(0);
          }}
          onPackageNameChange={packageName => store.getState().setPackageDetails({ packageName })}
        />

        {canUseJointCreation && (
          <div className={css.packageCreationWizard_jointOption}>
            <Checkbox
              labelRight={t("package-wizard.package.create-leagues")}
              checked={isJointCreationSelected}
              onChange={event => store.getState().setJointCreationSelected(event.target.checked)}
            />
            <Text
              className={css.packageCreationWizard_jointOptionText}
              type="sm"
              color="neutral-50"
            >
              {t("package-wizard.package.create-leagues-help")}
            </Text>
          </div>
        )}
        <div className={css.packageCreationWizard_actions}>
          <Button variant="ghost" disabled={isLoading} onClick={onCancel}>
            {t("create-package.btn-cancel")}
          </Button>
          <Button disabled={!isPackageDetailsValid || isLoading} onClick={handleStepOneContinue}>
            {isJointCreationSelected
              ? t("package-wizard.actions.continue")
              : t("create-package.btn-create")}
          </Button>
        </div>
      </div>

      {currentStep === "league-configuration" && (
        <LeagueConfigurationStep
          scenarioId={scenario}
          originId={origin}
          onBack={() => store.getState().goBack()}
          onCreate={submit}
          isSubmitting={isJointLoading}
          shouldContinueToGroups={shouldVisitInitialGroups}
        />
      )}

      {currentStep === "initial-groups" && (
        <InitialGroupsStep
          onBack={() => store.getState().goBack()}
          onCreate={submit}
          isSubmitting={isJointLoading}
        />
      )}

      {isLoading && <Loading />}
    </section>
  );
};

export default PackageCreationWizard;
